/* Single source of truth for per-route SEO on the 7 high-intent guide/hub
 * routes (STEP 31). Consumed by:
 *   - React (client head via GuidesPage / AdcPart1Pages, after mount)
 *   - craco dev-server middleware (preview: inject into initial HTML)
 *   - scripts/prerender-seo.js (production: write per-route static HTML)
 * CommonJS so Node (craco/prerender) and webpack can both load it. */

const SITE = "https://passadc.com";

// route -> { title (<=60, no "| PassADC"), description (<=155), h1 }
const GUIDE_SEO = {
  "/guides/adc-part-1-pass-rate": {
    title: "ADC Exam Pass Rate 2026: Official Part 1 & Part 2 Results",
    description:
      "Official ADC figures: written exam pass rate 12% in 2024-25 (404 of 3,325), down from 41%. Practical exam: 11%. Full FY2020-25 table explained.",
    h1: "ADC Exam Pass Rate: Official Part 1 and Part 2 Results",
  },
  "/guides/adc-part-1-cost": {
    title: "ADC Exam Fees 2026: Part 1 $2,122, Part 2 $4,775 AUD",
    description:
      "Official 2026 ADC fees (AUD): initial assessment $647, written exam $2,122, practical exam $4,775 per attempt. Plus USD, INR, PKR and total budget.",
    h1: "ADC Exam Fees 2026: Part 1 and Part 2 Costs in Full",
  },
  "/guides/what-is-the-adc-part-1": {
    title: "What Is the ADC Part 1? Format, Fees & Pass Rate 2026",
    description:
      "ADC Part 1 is a 280-question MCQ exam over 2 days. Fee AUD $2,122, pass rate 12% (2024-25). Next sitting 17-18 Mar 2027. Format, subjects and how to prep.",
    h1: "What Is the ADC Part 1 Exam? Format, Fees and Pass Rate (2026)",
  },
  "/guides/how-to-pass-adc-part-1": {
    title: "How to Pass ADC Part 1: A First-Attempt Plan (2026)",
    description:
      "A practical plan for the ADC Part 1 written exam: find weak disciplines, drill scenario MCQs, sit timed mocks, and book when your scores say you're ready.",
    h1: "How to Pass the ADC Part 1 Exam: The First-Attempt Playbook (2026)",
  },
  "/guides/adc-exam-cost-india": {
    title: "ADC Exam Cost for Indian Dentists 2026 (Fees in \u20B9)",
    description:
      "ADC fees for Indian dentists in 2026: initial assessment AUD $647 and written exam AUD $2,122, converted to INR, plus a realistic total budget and FX tips.",
    h1: "ADC Exam Cost for Indian Dentists 2026 (Fees in \u20B9, Total Budget, Payment Tips)",
  },
  "/guides/adc-exam-cost-pakistan": {
    title: "ADC Exam Cost for Pakistani Dentists 2026 (PKR Fees)",
    description:
      "ADC fees for Pakistani dentists in 2026: initial assessment AUD $647 and written exam AUD $2,122 in PKR, plus a realistic total budget and FX tips.",
    h1: "ADC Exam Cost for Pakistani Dentists 2026 (Fees in PKR, Total Budget, Payment Tips)",
  },
  "/adc-part-1": {
    title: "ADC Part 1 Question Bank, Mock Exams & Study Plan",
    description:
      "ADC-style MCQs with explanations, full mock exams and a readiness score across every ADC Part 1 discipline. Start free with 10 practice questions a day.",
    h1: "ADC Part 1 Preparation: Everything You Need in One Hub",
  },
};

const SEO_ROUTES = Object.keys(GUIDE_SEO);

function esc(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Inject a route's head tags + a crawler-visible H1 snapshot into the
 * compiled index.html string. Returns the original html if route is unknown. */
function injectSeo(html, route) {
  const r = (route || "").replace(/\/+$/, "") || "/";
  const seo = GUIDE_SEO[r];
  if (!seo) return html;
  const canonical = `${SITE}${r}`;
  const title = esc(seo.title);
  const desc = esc(seo.description);

  let out = html;
  // <title>
  out = out.replace(/<title>[\s\S]*?<\/title>/i, `<title>${title}</title>`);
  // description (replace if present, else inject)
  if (/<meta\s+name="description"[^>]*>/i.test(out)) {
    out = out.replace(/<meta\s+name="description"[^>]*>/i,
      `<meta name="description" content="${desc}"/>`);
  } else {
    out = out.replace(/<\/head>/i,
      `  <meta name="description" content="${desc}"/>\n</head>`);
  }
  // remove any pre-existing canonical to avoid duplicates
  out = out.replace(/<link\s+rel="canonical"[^>]*>/ig, "");
  const headTags = [
    `<link rel="canonical" href="${canonical}"/>`,
    `<meta property="og:title" content="${title}"/>`,
    `<meta property="og:description" content="${desc}"/>`,
    `<meta property="og:url" content="${canonical}"/>`,
    `<meta property="og:type" content="article"/>`,
    `<meta name="twitter:title" content="${title}"/>`,
    `<meta name="twitter:description" content="${desc}"/>`,
    `<meta name="robots" content="index, follow"/>`,
  ].join("\n  ");
  out = out.replace(/<\/head>/i, `  ${headTags}\n</head>`);

  // Crawler-visible H1 snapshot inside #root. React (createRoot) replaces #root
  // contents on mount, so there is no duplicate H1 and no hydration warning.
  const snapshot =
    `<div id="root"><div data-seo-snapshot="1">` +
    `<h1>${esc(seo.h1)}</h1><p>${desc}</p></div></div>`;
  out = out.replace(/<div id="root">\s*<\/div>/i, snapshot);
  return out;
}

module.exports = { SITE, GUIDE_SEO, SEO_ROUTES, injectSeo };
